/**
 * Full-screen 10s hold-to-open Wi-Fi setup gesture.
 *
 * Sets window.__doohSetupRequested on completion. The device gate polls this
 * via CDP every 2-3s and opens SoftAP when it sees a fresh request.
 */

import { useEffect, useRef } from 'react';
import {
  SETUP_HOLD_MOVE_TOLERANCE_PX,
  SETUP_HOLD_MS,
  requestSetup,
} from './playable-signal';

interface Point {
  x: number;
  y: number;
}

function pointFromEvent(e: PointerEvent): Point {
  return { x: e.clientX, y: e.clientY };
}

function movedTooFar(a: Point, b: Point, tol: number): boolean {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy > tol * tol;
}

/**
 * Attach a document-level pointer hold listener.
 * @param enabled When false, no listeners are attached (e.g. unpaired).
 */
export function useSetupHold(enabled = true): void {
  const originRef = useRef<Point | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const clear = () => {
      originRef.current = null;
      firedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const onDown = (e: PointerEvent) => {
      // Only primary button / touch.
      if (e.isPrimary === false) return;
      clear();
      originRef.current = pointFromEvent(e);
      timerRef.current = setTimeout(() => {
        if (!originRef.current || firedRef.current) return;
        firedRef.current = true;
        requestSetup();
      }, SETUP_HOLD_MS);
    };

    const onMove = (e: PointerEvent) => {
      if (!originRef.current || !timerRef.current) return;
      if (movedTooFar(originRef.current, pointFromEvent(e), SETUP_HOLD_MOVE_TOLERANCE_PX)) {
        clear();
      }
    };

    const onUp = () => {
      clear();
    };

    document.addEventListener('pointerdown', onDown, { passive: true });
    document.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerup', onUp, { passive: true });
    document.addEventListener('pointercancel', onUp, { passive: true });

    return () => {
      clear();
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
  }, [enabled]);
}
